// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { useState, forwardRef, type InputHTMLAttributes, type ReactElement, type ReactNode, type ComponentType } from 'react';
import { Eye, EyeOff, ChevronDown } from 'lucide-react';

// Paylaşılan form primitive'leri — tüm formlarda tutarlı görünüm (ikon önekli input, göz toggle, etiket/ipucu/hata).
// Tasarım token'ları: bg-card / border-border / primary-500 (tema cascade'ine uyar).

type IconType = ComponentType<{ className?: string }>;

interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({ label, hint, error, required, children, className = '' }: FieldProps): ReactElement {
  return (
    <div className={className}>
      {label && (
        <label className="block text-[13px] font-semibold text-foreground mb-1.5">
          {label}{required && <span className="text-destructive"> *</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="mt-1 text-[12px] text-destructive">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-[12px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

const baseInput =
  'w-full py-2.5 bg-card border border-border rounded-lg text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all';
const iconCls = 'absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none';

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: IconType;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { icon: Icon, className = '', ...rest },
  ref,
): ReactElement {
  if (!Icon) {
    return <input ref={ref} className={`${baseInput} px-3 ${className}`} {...rest} />;
  }
  return (
    <div className="relative">
      <Icon className={iconCls} />
      <input ref={ref} className={`${baseInput} pl-10 pr-3 ${className}`} {...rest} />
    </div>
  );
});

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  icon?: IconType;
}

export function PasswordInput({ icon: Icon, className = '', ...rest }: PasswordInputProps): ReactElement {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      {Icon && <Icon className={iconCls} />}
      <input
        type={show ? 'text' : 'password'}
        className={`${baseInput} ${Icon ? 'pl-10' : 'pl-3'} pr-10 ${className}`}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        tabIndex={-1}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

interface SelectInputProps extends InputHTMLAttributes<HTMLSelectElement> {
  icon?: IconType;
  children: ReactNode;
}

export function SelectInput({ icon: Icon, className = '', children, ...rest }: SelectInputProps): ReactElement {
  // Açılır liste olduğu belli olsun: sağda chevron + hafif vurgulu sağ kenar (native ok appearance-none ile gizli).
  return (
    <div className="relative">
      {Icon && <Icon className={iconCls} />}
      <select
        className={`${baseInput} ${Icon ? 'pl-10' : 'pl-3'} pr-9 appearance-none cursor-pointer ${className}`}
        {...rest as InputHTMLAttributes<HTMLSelectElement>}
      >
        {children}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
    </div>
  );
}
